import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FileTextIcon, TableIcon, XCircleIcon, EuroIcon, ListChecksIcon, DownloadIcon } from 'lucide-react';

// LET OP
// Hier stonden eerder twee klantcases met namen, quotes en
// resultaatpercentages die niet op echte klanten terug te voeren waren.
// Verzonnen testimonials en resultaatclaims zijn in Nederland verboden
// (Wet oneerlijke handelspraktijken, EU-Omnibusrichtlijn) en de ACM
// handhaaft daarop. Ze zijn vervangen door de daadwerkelijke inhoud van
// het rapport, want dat is controleerbaar en het overtuigt een zakelijke
// koper beter dan een percentage dat hij niet kan nagaan.
//
// Zodra er echte, met toestemming gedeelde resultaten zijn, kunnen die
// hier terug. Met naam, cijfer en een klant die het bevestigt.

const rapportOnderdelen = [{
  icon: TableIcon,
  titel: 'De zoekopdrachten die converteren',
  tekst: 'Per zoekterm het aantal klikken, het aantal aanvragen en het conversiepercentage. Dit is de lijst waar de bouwfase op wordt gebaseerd.'
}, {
  icon: XCircleIcon,
  titel: 'De zoekopdrachten die niets opleverden',
  tekst: 'Termen met volume die in jouw markt geen klant opleverden. Minstens zo waardevol, want dit is het budget dat je niet gaat uitgeven.'
}, {
  icon: EuroIcon,
  titel: 'Kosten per aanvraag per term',
  tekst: 'Wat een aanvraag je kost via advertenties. Daarmee kun je berekenen wat een organische positie op diezelfde term jaarlijks waard is.'
}, {
  icon: ListChecksIcon,
  titel: 'Het bouwplan',
  tekst: 'Welke pagina’s er moeten komen, in welke onderlinge structuur en in welke volgorde. Met een onderbouwing per pagina.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.23, 1, 0.32, 1]
    }
  }
};
export function PoC() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="bewijs" ref={ref} className="py-20 md:py-24 bg-white" data-id="element-296" data-mp="3e154qrcl6g3u">
      <div className="max-w-[1200px] mx-auto px-6" data-id="element-297" data-mp="3994wmbmd4gw6">
        <motion.div className="max-w-3xl mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-298" data-mp="397k2gn3ymzyu">
          <div className="section-label" data-id="element-299" data-mp="3s1fvludwbup0">De uitkomst</div>
          <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-brand-dark leading-tight tracking-tight mb-4" data-id="element-300" data-mp="22kybeaiyhmhe">
            Wat er na negentig dagen op tafel ligt
          </h2>
          <p className="text-brand-gray text-lg leading-relaxed" data-id="element-301" data-mp="1xohotkreng6f">
            Het CorePrint-rapport is het product van de meetfase. Het is
            jouw eigendom, ook als je daarna niets meer met ons doet.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-6 items-stretch mb-8" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-302" data-mp="4apqwt9zr4u3">
          {rapportOnderdelen.map(o => <motion.div key={o.titel} className="rounded-2xl border border-[#E8EDF8] bg-[#F8FAFC] p-8 flex flex-col" variants={itemVariants} data-id="element-303" data-mp="3vi299bmm1obl">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{
            background: '#FFFFFF',
            border: '1px solid rgba(0, 82, 204, 0.15)'
          }} data-id="element-304" data-mp="3hknc1kq48c92">
                <o.icon size={22} className="text-brand-primary" data-id="element-305" data-mp="1jrq7005bv77u" />
              </div>
              <h3 className="font-bold text-brand-dark text-lg mb-3" data-id="element-306" data-mp="v0kax5msdc31">
                {o.titel}
              </h3>
              <p className="text-brand-gray text-[0.96rem] leading-relaxed" data-id="element-307" data-mp="q3k0kiiwln64">
                {o.tekst}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Eerlijke stand van zaken */}
        <motion.div className="rounded-2xl border border-[#E8EDF8] bg-white p-8 md:p-10" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-308" data-mp="g6yuruz28818">
          <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-309" data-mp="3okxt339i85hw">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{
            background: '#EFF6FF',
            border: '1px solid rgba(0, 82, 204, 0.15)'
          }} data-id="element-310" data-mp="3kiswpwro8urk">
              <FileTextIcon size={22} className="text-brand-primary" data-id="element-311" data-mp="240k8luheozw6" />
            </div>
            <div data-id="element-312" data-mp="291zu1nnhx8f3">
              <h3 className="font-bold text-brand-dark text-xl mb-3" data-id="element-313" data-mp="28zdn9q8fiwxx">
                CorePrint draait nu met een eerste groep bedrijven
              </h3>
              <p className="text-brand-gray leading-relaxed mb-4 max-w-3xl" data-id="element-314" data-mp="1gdkjpefb7ea3">
                We zetten hier geen resultaatpercentages neer die je niet kunt
                nagaan. CorePrint is een nieuwe methodiek en die wordt op dit
                moment bewezen met een geselecteerde groep opdrachtgevers. Wat
                we wél kunnen laten zien is het werk van Noord Digital zelf: ruim
                twintig opgeleverde projecten en de beoordelingen van die
                klanten.
              </p>
              <p className="text-brand-gray leading-relaxed mb-6 max-w-3xl" data-id="element-315" data-mp="12k39wlcfdfx9">
                Wil je een voorbeeldrapport zien voordat je beslist? Vraag er
                gerust om in het kennismakingsgesprek. Dan zie je precies welk
                document je na negentig dagen krijgt.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-316" data-mp="1496sqoe6nuu0">
                <a href="#gesprek" className="inline-flex items-center gap-2 bg-brand-primary text-white font-semibold px-7 py-4 rounded-lg hover:opacity-90 transition-opacity duration-200" data-id="element-317" data-mp="zboqthyl2fvi">
                  <DownloadIcon size={17} data-id="element-318" data-mp="7lk6fjatpsua" />
                  Vraag een voorbeeldrapport
                </a>
                <a href="https://noorddigital.nl/#client-cases" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#E8EDF8] text-brand-dark font-semibold px-7 py-4 rounded-lg hover:bg-[#F8FAFC] transition-colors duration-200" data-id="element-319" data-mp="lll7ayr3909r">
                  Bekijk het werk van Noord Digital
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}